import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';

import { objectClassApi } from '@/api';
import { QUERY_KEY_CONST } from '@/constant';
import type { LabelingApi } from '@/type';

export const useSuperObjectClassListQuery = () => {
  const result = useQuery({
    queryKey: QUERY_KEY_CONST.LABELLING_KEYS.getObjectClassList({
      classType: 'Super',
    }),
    queryFn: () =>
      objectClassApi
        .getObjectClassList({ classType: 'Super' })
        .then(res => res.data.resultData),
    staleTime: 1000 * 60,
  });

  return result;
};

export const useExtendObjectClassListQuery = () => {
  const result = useQuery({
    queryKey: QUERY_KEY_CONST.LABELLING_KEYS.getObjectClassList({
      classType: 'Extend',
    }),
    queryFn: () =>
      objectClassApi
        .getObjectClassList({ classType: 'Extend' })
        .then(res => res.data.resultData),
  });

  return result;
};

export const useAllObjectClassListQuery = () => {
  const superObjectClassListQuery = useSuperObjectClassListQuery();

  const allObjectClassList = useMemo(() => {
    if (!superObjectClassListQuery.data) return undefined;

    return superObjectClassListQuery.data.flatMap(item => [
      item,
      ...(item.extendClass ?? []),
    ]);
  }, [superObjectClassListQuery.data]);

  return {
    ...superObjectClassListQuery,
    data: allObjectClassList,
  };
};

export const useCreateObjectClassMutation = () => {
  const queryClient = useQueryClient();
  const result = useMutation({
    mutationFn: (data: LabelingApi.CreateObjectClass.Request) =>
      objectClassApi.createObjectClass(data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: QUERY_KEY_CONST.LABELLING_KEYS.getObjectClassList(),
      });
    },
  });

  return result;
};

export const useUpdateObjectClassMutation = () => {
  const queryClient = useQueryClient();
  const result = useMutation({
    mutationFn: (data: LabelingApi.UpdateObjectClass.Request) =>
      objectClassApi.updateObjectClass(data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: QUERY_KEY_CONST.LABELLING_KEYS.getObjectClassList(),
      });
    },
  });

  return result;
};

export const useReorderExtendClassMutation = () => {
  const queryClient = useQueryClient();
  const result = useMutation({
    mutationFn: (data: LabelingApi.ReorderExtendClass.Request) =>
      objectClassApi.reorderExtendClass({
        pObjectClassID: data.pObjectClassID,
        prevID: String(data.prevID),
        nextID: data.nextID ? String(data.nextID) : null,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: QUERY_KEY_CONST.LABELLING_KEYS.getObjectClassList(),
      });
    },
  });

  return result;
};

export const useGetExtendClassIconListQuery = (
  params?: LabelingApi.GetExtendClassIconList.Request,
) => {
  const result = useQuery({
    queryKey: QUERY_KEY_CONST.ICON_KEYS.getObjectClassIconList(params),
    queryFn: () =>
      objectClassApi
        .getExtendClassIconList(params)
        .then(res => res.data.resultData),
  });

  return result;
};

export const useCreateExtendClassIconMutation = () => {
  const queryClient = useQueryClient();
  const result = useMutation({
    mutationFn: (data: LabelingApi.CreateExtendClassIcon.Request) =>
      objectClassApi.createObjectClassIcon(data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: QUERY_KEY_CONST.ICON_KEYS.getObjectClassIconList(),
      });
      queryClient.invalidateQueries({
        queryKey: QUERY_KEY_CONST.LABELLING_KEYS.getObjectClassList(),
      });
    },
  });

  return result;
};

export const useDeleteIconMutation = () => {
  const queryClient = useQueryClient();
  const result = useMutation({
    mutationFn: (data: LabelingApi.DeleteIcon.Request) =>
      objectClassApi.deleteIcon(data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: QUERY_KEY_CONST.ICON_KEYS.getObjectClassIconList(),
      });
    },
  });

  return result;
};
